import { useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

// Activity categories with their items and icons
const activityCategories = {
  'Outdoor Recreation': {
    icon: 'person-hiking',
    items: [
      'hiking',
      'biking',
      'road biking',
      'mountain biking',
      'camping',
      'backcountry camping',
      'car or front country camping',
      'rv camping',
      'rock climbing',
      'canyoneering',
      'scenic driving',
      'auto and atv',
      'off-road driving',
      'horseback riding',
      'caving',
      'geocaching',
      'orienteering',
      'picnicking',
      'playground',
      'off-roading',
      'mountain climbing',
      'horse trekking',
      'compass',
      'gps',
    ],
  },
  'Water Activities': {
    icon: 'water',
    items: [
      'swimming',
      'fishing',
      'fly fishing',
      'freshwater fishing',
      'saltwater fishing',
      'kayaking',
      'canoeing',
      'paddling',
      'boating',
      'sailing',
      'surfing',
      'scuba diving',
      'snorkeling',
      'tubing',
      'water skiing',
      'whitewater rafting',
      'stand up paddleboarding',
      'jet skiing',
      'boat tour',
    ],
  },
  'Winter Activities': {
    icon: 'snowflake',
    items: [
      'skiing',
      'cross-country skiing',
      'downhill skiing',
      'snowboarding',
      'snowshoeing',
      'snowmobiling',
      'ice skating',
      'ice climbing',
      'sledding',
      'snow play',
    ],
  },
  'Wildlife & Nature': {
    icon: 'binoculars',
    items: [
      'wildlife watching',
      'birdwatching',
      'bird watching',
      'park film',
      'stargazing',
      'astronomy',
      'wildflower viewing',
      'nature walks',
      'botanical garden',
      'tree viewing',
      'planetarium',
      'gathering and foraging',
    ],
  },
  'Cultural & Educational': {
    icon: 'landmark',
    items: [
      'guided tours',
      'self-guided tours - walking',
      'museum exhibits',
      'living history',
      'bookstore and/or park store',
      'arts and culture',
      'hands-on',
      'craft demonstrations',
      'cultural demonstrations',
      'first person interpretation',
      'ranger led programs',
      'junior ranger program',
      'citizen science',
      'research',
      'archeology',
      'reenactments',
      'live music',
      'theater',
      'historic weapons',
      'arts and crafts',
      'bus/shuttle guided tour',
      'bookstore and park store',
    ],
  },
  'Sports & Recreation': {
    icon: 'volleyball',
    items: [
      'golfing',
      'tennis',
      'team sports',
      'disc golf',
      'hunting',
      'archery',
      'shooting',
      'sports',
      'mini-golf',
    ],
  },
};

// Icon mapping for individual activities
const activityIcons = {
  hiking: 'person-hiking',
  biking: 'person-biking',
  'road biking': 'person-biking',
  'mountain biking': 'person-biking',
  camping: 'campground',
  'backcountry camping': 'tent',
  'car or front country camping': 'caravan',
  'rv camping': 'caravan',
  fishing: 'fish',
  'fly fishing': 'fish',
  'freshwater fishing': 'fish',
  'saltwater fishing': 'fish',
  swimming: 'person-swimming',
  kayaking: 'sailboat',
  canoeing: 'sailboat',
  paddling: 'sailboat',
  boating: 'ship',
  sailing: 'sailboat',
  'scuba diving': 'mask-snorkel',
  snorkeling: 'mask-snorkel',
  skiing: 'person-skiing',
  'cross-country skiing': 'person-skiing-nordic',
  'downhill skiing': 'person-skiing',
  snowboarding: 'person-snowboarding',
  snowshoeing: 'shoe-prints',
  snowmobiling: 'snowplow',
  'wildlife watching': 'binoculars',
  birdwatching: 'dove',
  'bird watching': 'dove',
  stargazing: 'star',
  astronomy: 'moon',
  'guided tours': 'users',
  'self-guided tours - walking': 'map',
  'museum exhibits': 'building-columns',
  'living history': 'scroll',
  'junior ranger program': 'award',
  'rock climbing': 'mountain',
  'horseback riding': 'horse',
  'scenic driving': 'car',
  'auto and atv': 'car-side',
  picnicking: 'utensils',
  golfing: 'golf-ball-tee',
  hunting: 'bullseye',
  caving: 'hill-rockslide',
  surfing: 'water',
  'stand up paddleboarding': 'sailboat',
  'whitewater rafting': 'anchor',
};

function CategorySection({
  title,
  icon,
  activities = [],
  defaultOpen = false,
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  if (!activities || activities.length === 0) return null;

  return (
    <div className="w-full">
      <div className="mx-auto w-full rounded bg-white border-beige-100/40 border-2 mt-2 p-2">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex w-full justify-between items-center rounded bg-beige-300 px-4 py-2 pt-3 text-left text-sm transition-all hover:bg-beige-100/30 focus:outline-none"
        >
          <div className="flex items-center gap-3">
            <i
              className={`fa fa-${icon} text-green-100 text-base w-5`}
              aria-hidden="true"
            />
            <span className="font-semibold text-brown-200">{title}</span>
            <span className="text-xs text-brown-100/70 bg-white/60 px-2 py-0.5 rounded-full">
              {activities.length}
            </span>
          </div>
          <ChevronDownIcon
            className={clsx(
              'w-5 h-5 text-brown-100 transition-transform duration-200',
              isOpen && 'rotate-180'
            )}
          />
        </button>

        <div
          className={clsx(
            'overflow-hidden transition-all duration-200',
            isOpen ? 'max-h-[500px]' : 'max-h-0'
          )}
        >
          <div className="px-4 pt-4 pb-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-1">
              {activities.map((activity, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-2 text-sm text-brown-200 py-1"
                >
                  <i
                    className={`fa fa-${activityIcons[activity.toLowerCase()] || 'circle'} text-brown-300 ${activityIcons[activity.toLowerCase()] ? 'text-xs' : 'text-[5px]'} w-4`}
                    aria-hidden="true"
                  />
                  <span>{activity}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Activities({ activities }) {
  if (!activities || activities.length === 0) return null;

  // Group activities by category
  const groupedActivities = {};
  const uncategorized = [];

  activities.forEach((activity) => {
    const activityName = activity.name.toLowerCase();
    let found = false;

    for (const [category, config] of Object.entries(activityCategories)) {
      if (
        config.items.some(
          (item) => activityName.includes(item) || item.includes(activityName)
        )
      ) {
        if (!groupedActivities[category]) {
          groupedActivities[category] = {
            icon: config.icon,
            items: [],
          };
        }
        groupedActivities[category].items.push(activity.name);
        found = true;
        break;
      }
    }

    if (!found) {
      uncategorized.push(activity.name);
    }
  });

  // Sort categories by number of items (most items first)
  const sortedCategories = Object.entries(groupedActivities).sort(
    (a, b) => b[1].items.length - a[1].items.length
  );

  return (
    <div>
      {sortedCategories.map(([category, config], index) => (
        <CategorySection
          key={category}
          title={category}
          icon={config.icon}
          activities={config.items}
          defaultOpen={index === 0}
        />
      ))}

      {uncategorized.length > 0 && (
        <CategorySection
          title="Other Activities"
          icon="list"
          activities={uncategorized}
        />
      )}
    </div>
  );
}
